import { useEffect, useState } from "react";
import { z } from "zod";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { useAuth } from "../../contexts/AuthContext";
import { useProfile, useFxRates } from "../../hooks/queries";
import { profileService } from "../../services/profileService";
import { authService } from "../../services/authService";
import { digestService } from "../../services/digestService";
import { tokenStore } from "../../lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { User2, Save, Loader2, Mail, Languages, Coins, Briefcase, Sparkles, Lock, AtSign, Send, Eye, Shield, BellOff, Settings2, CheckCircle2, FileText, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { LockedBadge } from "../../components/LockedBadge";
import { useGatedAction } from "../../hooks/useGatedAction";
import { useEntitlements } from "../../hooks/useEntitlements";
import { Switch } from "../../components/ui/switch";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";
import { ARCHETYPES } from "../../lib/archetypes";

export default function Profile() {
  const { lang, setLang, t } = useLang();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const { data: profile, isLoading } = useProfile();
  const { data: fx } = useFxRates();
  const currencies = Object.keys(fx?.rates || { USD: 1, EUR: 1, COP: 1, MXN: 1 }).sort();

  // iter-169zze · personal info migrated to <FormField/> + useZodForm.
  // Schema lives in the function body so the Spanish/English copy can
  // honour the current ``lang`` without forcing a global setErrorMap
  // refetch when the user toggles the language (the global error map
  // covers all OTHER Zod schemas via LangContext).
  const ProfileSchema = z.object({
    full_name: z.string().trim().min(1, tx(lang, "Full name is required.", "El nombre completo es obligatorio.", "El nombre completo es obligatorio.")),
    professional_activity: z.string().trim().min(1, tx(lang, "Professional activity is required.", "La actividad profesional es obligatoria.", "La actividad profesional es obligatoria.")),
    archetype_slug: z.string().optional(),
    avg_monthly_income: z.string().refine(
      (v) => v === "" || (!Number.isNaN(Number(v)) && Number(v) >= 0),
      { message: tx(lang, "Income must be a number ≥ 0.", "El ingreso debe ser un número ≥ 0.", "El ingreso debe ser un número ≥ 0.") },
    ),
    timezone: z.string(),
  });

  const profileForm = useZodForm(ProfileSchema, {
    full_name: "",
    professional_activity: "",
    archetype_slug: "",
    avg_monthly_income: "",
    timezone: "",
  }, { formId: "profile-basic" });

  useEffect(() => {
    if (profile) {
      profileForm.reset({
        full_name: profile.full_name || "",
        professional_activity: profile.professional_activity || "",
        archetype_slug: profile.archetype_slug || "",
        avg_monthly_income: profile.avg_monthly_income != null ? String(profile.avg_monthly_income) : "",
        timezone: profile.timezone || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // iter-213.1 — Track whether the form has been hydrated from the
  // server profile. Without this guard the Radix <Select> for
  // `archetype_slug` could render once with the empty default before
  // useEffect fires `profileForm.reset(...)`, leading to a momentary
  // "Elige un perfil" placeholder even when the user already has a
  // saved archetype. Gating render on `archetypeHydrated` ensures we
  // skip that flash entirely.
  const archetypeHydrated = profile && (
    profileForm.values.archetype_slug === (profile.archetype_slug || "")
  );

  const save = useMutation({
    mutationFn: (values) =>
      profileService.update({
        full_name: values.full_name.trim(),
        professional_activity: values.professional_activity.trim(),
        archetype_slug: values.archetype_slug || null,
        avg_monthly_income: values.avg_monthly_income === "" ? null : Number(values.avg_monthly_income),
        timezone: values.timezone || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(tx(lang, "Profile updated", "Perfil actualizado", "Perfil actualizado"));
    },
    onError: () => toast.error(tx(lang, "Save failed", "No se pudo guardar", "No se pudo guardar")),
  });

  // Currency is intentionally NOT part of the form state: it shares a
  // single source of truth with the sidebar `CurrencySwitcher` (both
  // edit `profile.currency`) and saves immediately on change so the two
  // controls always show the same value.
  const onCurrencyChange = async (next) => {
    if (!next || next === profile?.currency) return;
    try {
      await profileService.update({ currency: next });
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`${next} ✓`);
    } catch (e) {
      console.error("set currency failed", e);
      toast.error(tx(lang, "Couldn't update currency", "No se pudo actualizar la moneda", "Não foi possível atualizar a moeda"));
    }
  };

  const onSaveProfile = profileForm.handleSubmit(async (values) => {
    await save.mutateAsync(values);
  });

  const onLanguageChange = (next) => {
    setLang(next);
    const labels = { en: "English", es: "Español", pt: "Português (BR)" };
    toast.success(tx(lang,
      `Chat language: ${labels[next]}`,
      `Idioma del chat: ${labels[next]}`,
      `Idioma do chat: ${labels[next]}`,
    ));
    // Refresh /me so the AuthContext sees the new language.
    refresh && refresh();
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto fade-up">
        <PageHeader
          eyebrow={tx(lang, "Account", "Cuenta", "Cuenta")}
          title={tx(lang, "Your profile", "Tu perfil", "Tu perfil")}
          subtitle={tx(lang, "Edit your basics, chat language and primary currency.", "Edita tus datos básicos, idioma del chat y moneda principal.", "Edita tus datos básicos, idioma del chat y moneda principal.")}
          testid="profile-header"
        />
        <div className="space-y-6">

        {/* Account */}
        <Card className="lf-surface p-6" data-testid="profile-account-card">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-4 w-4 text-slate-500" />
            <h2 className="font-semibold text-slate-900">{tx(lang, "Account", "Cuenta", "Cuenta")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
              <Input
                data-testid="profile-email"
                value={user?.email || ""}
                disabled
                className="bg-slate-50 cursor-not-allowed"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                {tx(lang, "Email can't be edited yet.", "El email no se puede editar todavía.", "El email no se puede editar todavía.")}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                {tx(lang, "Member since", "Miembro desde", "Miembro desde")}
              </Label>
              <Input
                value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : ""}
                disabled
                className="bg-slate-50 cursor-not-allowed"
              />
            </div>
          </div>
        </Card>

        {/* Basic profile · iter-169zze migrated to <FormField/> + useZodForm.
            Inline validation (Zod) + auto telemetry + a11y wiring out of the box. */}
        <Card className="p-6 rounded-2xl border-slate-200" data-testid="profile-basic-card">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="h-4 w-4 text-slate-500" />
            <h2 className="font-semibold text-slate-900">
              {tx(lang, "Personal info", "Información personal", "Información personal")}
            </h2>
          </div>
          {isLoading ? (
            <div className="text-sm text-slate-400 py-4 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {tx(lang, "Loading…", "Cargando…", "Cargando…")}
            </div>
          ) : (
            <form onSubmit={onSaveProfile} noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  label={tx(lang, "Full name", "Nombre completo", "Nombre completo")}
                  name="full_name"
                  required
                  error={profileForm.errors.full_name && (profileForm.touched.full_name || profileForm.submitCount > 0) ? profileForm.errors.full_name : undefined}
                >
                  <FormField.Input
                    {...profileForm.getFieldProps("full_name")}
                    placeholder="Laura Gómez"
                    data-testid="profile-full-name"
                  />
                </FormField>
                <FormField
                  label={tx(lang, "Professional activity", "Actividad profesional", "Actividad profesional")}
                  name="professional_activity"
                  required
                  error={profileForm.errors.professional_activity && (profileForm.touched.professional_activity || profileForm.submitCount > 0) ? profileForm.errors.professional_activity : undefined}
                >
                  <FormField.Input
                    {...profileForm.getFieldProps("professional_activity")}
                    placeholder={tx(lang, "Freelance UX designer", "Diseñadora UX freelance", "Diseñadora UX freelance")}
                    data-testid="profile-activity"
                  />
                </FormField>
                <div className="sm:col-span-2" data-testid="profile-archetype-row">
                  <FormField
                    label={
                      <span className="flex items-center gap-1">
                        <UserCheck className="h-3 w-3" />
                        {tx(lang, "Which one describes you best?", "¿Cuál te describe mejor?", "Qual te descreve melhor?")}
                      </span>
                    }
                    name="archetype_slug"
                    hint={tx(lang,
                      "Helps us tailor the dashboard, alerts and weekly digest to your profile.",
                      "Nos ayuda a personalizar el dashboard, alertas y resumen semanal según tu perfil.",
                      "Nos ajuda a personalizar o dashboard, alertas e resumo semanal pelo seu perfil.",
                    )}
                  >
                    {archetypeHydrated ? (
                      <Select
                        value={profileForm.values.archetype_slug || "__none__"}
                        onValueChange={(v) => {
                          // Radix Select fires onValueChange("") when its
                          // controlled `value` prop transitions to a value
                          // it hasn't seen yet on mount. Guard against
                          // that spurious fire — only accept the change if
                          // we got a real string from the user (which is
                          // always either "__none__" or an archetype slug).
                          if (!v) return;
                          profileForm.setValue("archetype_slug", v === "__none__" ? "" : v);
                        }}
                      >
                      <SelectTrigger data-testid="profile-archetype-trigger">
                        <SelectValue placeholder={tx(lang, "Pick a profile (optional)", "Elige un perfil (opcional)", "Escolha um perfil (opcional)")} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__" data-testid="profile-archetype-none">
                          {tx(lang, "— Don't specify —", "— Sin especificar —", "— Sem especificar —")}
                        </SelectItem>
                        {ARCHETYPES.map((a) => {
                          const Icon = a.icon;
                          return (
                            <SelectItem
                              key={a.slug}
                              value={a.slug}
                              data-testid={`profile-archetype-opt-${a.slug}`}
                            >
                              <span className="flex items-center gap-2">
                                <Icon className="h-3.5 w-3.5 text-slate-500" />
                                <span className="font-medium">{a.label[lang] || a.label.es}</span>
                                <span className="text-[11px] text-slate-400 hidden sm:inline">· {a.hint[lang] || a.hint.es}</span>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    ) : (
                      <div className="h-9 rounded-md border border-slate-200 bg-slate-50 animate-pulse" data-testid="profile-archetype-skeleton" />
                    )}
                  </FormField>
                </div>
                <FormField
                  label={
                    <span className="flex items-center gap-1">
                      <Coins className="h-3 w-3" />
                      {tx(lang, "Primary currency", "Moneda principal", "Moneda principal")}
                    </span>
                  }
                  name="currency"
                  hint={tx(lang,
                    "Same as the currency switcher in the sidebar. Saved instantly.",
                    "Es la misma del switcher del menú lateral. Se guarda al instante.",
                    "É a mesma do switcher do menu lateral. Salva automaticamente.",
                  )}
                >
                  <Select
                    key={profile?.currency || "USD"}
                    value={profile?.currency || "USD"}
                    onValueChange={onCurrencyChange}
                  >
                    <SelectTrigger data-testid="profile-currency">
                      <SelectValue placeholder="USD" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {currencies.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`profile-currency-opt-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField
                  label={tx(lang, "Average monthly income", "Ingreso mensual promedio", "Ingreso mensual promedio")}
                  name="avg_monthly_income"
                  error={profileForm.errors.avg_monthly_income && (profileForm.touched.avg_monthly_income || profileForm.submitCount > 0) ? profileForm.errors.avg_monthly_income : undefined}
                >
                  <FormField.Input
                    {...profileForm.getFieldProps("avg_monthly_income")}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    placeholder="6000000"
                    data-testid="profile-avg-income"
                  />
                </FormField>
                <div className="sm:col-span-2">
                  <FormField
                    label={tx(lang, "Timezone (IANA)", "Zona horaria (IANA)", "Zona horaria (IANA)")}
                    name="timezone"
                    hint={tx(lang, "Controls when your weekly digest fires (local Sunday 20:00). Empty falls back to UTC.", "Controla cuándo te llega el resumen semanal (domingo 20:00 local). Si lo dejas vacío, usa UTC.", "Controla cuándo te llega o resumo semanal (domingo 20:00 local). Se ficar vazio, usa UTC.")}
                  >
                    <Input
                      data-testid="profile-timezone"
                      list="timezone-options"
                      value={profileForm.values.timezone}
                      onChange={(e) => profileForm.setValue("timezone", e.target.value)}
                      placeholder="America/Bogota"
                    />
                    <datalist id="timezone-options">
                      <option value="America/Bogota" />
                      <option value="America/Mexico_City" />
                      <option value="America/Argentina/Buenos_Aires" />
                      <option value="America/Santiago" />
                      <option value="America/Lima" />
                      <option value="America/Sao_Paulo" />
                      <option value="America/New_York" />
                      <option value="America/Los_Angeles" />
                      <option value="Europe/Madrid" />
                      <option value="Europe/London" />
                      <option value="UTC" />
                    </datalist>
                  </FormField>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <SubmitButton
                  submitting={profileForm.submitting}
                  testid="profile-save"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  loadingText={tx(lang, "Saving…", "Guardando…", "Guardando…")}
                >
                  <Save className="h-4 w-4" />
                  {tx(lang, "Save changes", "Guardar cambios", "Guardar cambios")}
                </SubmitButton>
              </div>
            </form>
          )}
        </Card>

        {/* Language */}
        <Card className="p-6 rounded-2xl border-slate-200" data-testid="profile-lang-card">
          <div className="flex items-center gap-2 mb-4">
            <Languages className="h-4 w-4 text-slate-500" />
            <h2 className="font-semibold text-slate-900">
              {tx(lang, "Language", "Idioma", "Idioma")}
            </h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            {tx(lang, "Your selection controls the interface AND every Lumfi AI chat reply (web, Telegram & WhatsApp).", "Tu selección controla la interfaz Y las respuestas del chat de Lumfi AI (web, Telegram y WhatsApp).", "Tu selección controla la interfaz Y las respuestas del chat de Lumfi AI (web, Telegram y WhatsApp).")}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              data-testid="profile-lang-en"
              variant={lang === "en" ? "default" : "outline"}
              onClick={() => onLanguageChange("en")}
              className={lang === "en" ? "bg-slate-900 text-white" : ""}
            >
              English
            </Button>
            <Button
              type="button"
              data-testid="profile-lang-es"
              variant={tx(lang, "outline", "default", "default")}
              onClick={() => onLanguageChange("es")}
              className={tx(lang, "", "bg-slate-900 text-white", "bg-slate-900 text-white")}
            >
              Español
            </Button>
            <Button
              type="button"
              data-testid="profile-lang-pt"
              variant={lang === "pt" ? "default" : "outline"}
              onClick={() => onLanguageChange("pt")}
              className={lang === "pt" ? "bg-slate-900 text-white" : ""}
            >
              Português (BR)
            </Button>
          </div>
        </Card>

        {/* Email change */}
        <ChangeEmailCard lang={lang} userEmail={user?.email} refresh={refresh} />

        {/* Email Privacy & Preferences */}
        <EmailPrivacyCard lang={lang} profile={profile} qc={qc} />

        {/* Password change */}
        <ChangePasswordCard lang={lang} />

        {/* Weekly digest preview & test */}
        <DigestPreviewCard lang={lang} />

        {/* iter-170af — Monthly Advanced PDF email opt-in/out */}
        <MonthlyPdfEmailCard lang={lang} profile={profile} qc={qc} />

        {/* Pointer to advanced settings */}
        <Card className="p-6 rounded-2xl border-sky-200 bg-sky-50/40" data-testid="profile-advanced-card">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-2xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">
                {tx(lang, "Financial personality & recommendations", "Personalidad financiera & recomendaciones", "Personalidad financiera & recomendaciones")}
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                {tx(lang, "Edit risk tolerance, investment horizon, monthly expenses, dependents and FX alert threshold. Grounds the advisor's advice in your profile.", "Edita tolerancia al riesgo, horizonte de inversión, gastos mensuales, dependientes y el umbral de alerta FX. Aterriza los consejos del advisor a tu perfil.", "Edita tolerancia al riesgo, horizonte de inversión, gastos mensuales, dependientes y el umbral de alerta FX. Aterriza los consejos del advisor a tu perfil.")}
              </p>
              <Link
                to="/recommendations"
                data-testid="profile-go-recs"
                className="inline-flex items-center mt-3 text-sm font-medium text-sky-700 hover:text-sky-900"
              >
                {tx(lang, "Go to recommendations →", "Ir a recomendaciones →", "Ir a recomendaciones →")}
              </Link>
            </div>
          </div>
        </Card>
        </div>
      </div>
    </Layout>
  );
}


// ---------- ChangeEmailCard ----------
function ChangeEmailCard({ lang, userEmail, refresh }) {
  const isEs = lang === "es";
  const ChangeEmailSchema = z.object({
    new_email: z.string().trim().email(isEs ? "Email inválido." : "Invalid email."),
    current_password: z.string().min(1, isEs ? "Contraseña requerida." : "Password required."),
  });
  const form = useZodForm(ChangeEmailSchema, { new_email: "", current_password: "" }, { formId: "profile-change-email" });

  const m = useMutation({
    mutationFn: (vals) => authService.changeEmail(vals.new_email.trim(), vals.current_password),
    onSuccess: (data) => {
      // iter-169zzo — change-email is now a 2-phase magic-link flow.
      // The user MUST click the link in their NEW inbox to activate.
      // The old session keeps working until then.
      toast.success(
        tx(lang,
          `We sent a magic link to ${data?.sent_to || "the new email"}. Click it within 30 minutes to confirm the change.`,
          `Te enviamos un enlace de confirmación a ${data?.sent_to || "el email nuevo"}. Haz click dentro de 30 minutos para activar el cambio.`,
          `Enviamos um link de confirmação para ${data?.sent_to || "o novo email"}. Clique em 30 minutos para ativar a mudança.`,
        ),
        { duration: 10000 },
      );
      form.reset({ new_email: "", current_password: "" });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || "error";
      // Surface server-side rejections as INLINE field errors on the
      // matching input (instead of a vague top-of-page toast). Keeps the
      // pattern consistent with client-side Zod failures.
      if (detail === "current_password_incorrect") {
        form.setError("current_password", isEs ? "Contraseña incorrecta" : "Wrong password");
        return;
      }
      if (detail === "email_already_in_use") {
        form.setError("new_email", isEs ? "Ese email ya está en uso" : "Email already in use");
        return;
      }
      if (detail === "new_email_same_as_current") {
        form.setError("new_email", isEs ? "Es el mismo email" : "Same email");
        return;
      }
      toast.error(detail);
    },
  });

  const onSubmit = form.handleSubmit((vals) => m.mutateAsync(vals));

  return (
    <Card className="p-6 rounded-2xl border-slate-200" data-testid="profile-email-card-edit">
      <div className="flex items-center gap-2 mb-4">
        <AtSign className="h-4 w-4 text-slate-500" />
        <h2 className="font-semibold text-slate-900">
          {isEs ? "Cambiar email" : "Change email"}
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        {isEs
          ? `Email actual: ${userEmail || "—"}. Necesitas tu contraseña actual para confirmar el cambio.`
          : `Current email: ${userEmail || "—"}. You need your current password to confirm the change.`}
      </p>
      <form onSubmit={onSubmit} noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            label={isEs ? "Nuevo email" : "New email"}
            name="new_email"
            required
            error={form.errors.new_email && (form.touched.new_email || form.submitCount > 0) ? form.errors.new_email : undefined}
          >
            <FormField.Input
              {...form.getFieldProps("new_email")}
              type="email"
              placeholder="new@example.com"
              autoComplete="off"
              data-testid="profile-new-email"
            />
          </FormField>
          <FormField
            label={isEs ? "Contraseña actual" : "Current password"}
            name="current_password"
            required
            error={form.errors.current_password && (form.touched.current_password || form.submitCount > 0) ? form.errors.current_password : undefined}
          >
            <FormField.Input
              {...form.getFieldProps("current_password")}
              type="password"
              autoComplete="current-password"
              data-testid="profile-email-current-pw"
            />
          </FormField>
        </div>
        <div className="mt-4 flex justify-end">
          <SubmitButton
            submitting={form.submitting || m.isPending}
            testid="profile-change-email-submit"
            className="bg-sky-600 hover:bg-sky-700 text-white"
            loadingText={isEs ? "Cambiando…" : "Changing…"}
          >
            <AtSign className="h-4 w-4" />
            {isEs ? "Cambiar email" : "Change email"}
          </SubmitButton>
        </div>
      </form>
    </Card>
  );
}

// ---------- ChangePasswordCard ----------
function ChangePasswordCard({ lang }) {
  const isEs = lang === "es";
  const ChangePasswordSchema = z.object({
    current_password: z.string().min(1, isEs ? "Contraseña actual requerida." : "Current password required."),
    new_password: z.string().min(8, isEs ? "Mínimo 8 caracteres." : "Minimum 8 characters."),
    confirm: z.string().min(1),
  }).refine((vals) => vals.new_password === vals.confirm, {
    message: isEs ? "No coinciden." : "Doesn't match.",
    path: ["confirm"],
  });
  const form = useZodForm(ChangePasswordSchema, {
    current_password: "", new_password: "", confirm: "",
  }, { formId: "profile-change-password" });

  const m = useMutation({
    mutationFn: (vals) => authService.changePassword(vals.current_password, vals.new_password),
    onSuccess: (data) => {
      if (data?.access_token) tokenStore.set(data.access_token);
      toast.success(isEs ? "Contraseña actualizada" : "Password updated");
      form.reset({ current_password: "", new_password: "", confirm: "" });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || "error";
      if (detail === "current_password_incorrect") {
        form.setError("current_password", isEs ? "Contraseña actual incorrecta" : "Wrong current password");
        return;
      }
      if (detail === "new_password_same_as_current") {
        form.setError("new_password", isEs ? "La nueva contraseña es igual a la actual" : "Same as current password");
        return;
      }
      toast.error(detail);
    },
  });

  const onSubmit = form.handleSubmit((vals) => m.mutateAsync(vals));

  return (
    <Card className="p-6 rounded-2xl border-slate-200" data-testid="profile-password-card">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="h-4 w-4 text-slate-500" />
        <h2 className="font-semibold text-slate-900">
          {isEs ? "Cambiar contraseña" : "Change password"}
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        {isEs
          ? "Mínimo 8 caracteres. Tu sesión sigue activa después del cambio."
          : "Minimum 8 characters. Your session stays active after the change."}
      </p>
      <form onSubmit={onSubmit} noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FormField
            label={isEs ? "Contraseña actual" : "Current password"}
            name="current_password"
            required
            error={form.errors.current_password && (form.touched.current_password || form.submitCount > 0) ? form.errors.current_password : undefined}
          >
            <FormField.Input
              {...form.getFieldProps("current_password")}
              type="password"
              autoComplete="current-password"
              data-testid="profile-pw-current"
            />
          </FormField>
          <FormField
            label={isEs ? "Nueva contraseña" : "New password"}
            name="new_password"
            required
            error={form.errors.new_password && (form.touched.new_password || form.submitCount > 0) ? form.errors.new_password : undefined}
          >
            <FormField.Input
              {...form.getFieldProps("new_password")}
              type="password"
              autoComplete="new-password"
              data-testid="profile-pw-new"
            />
          </FormField>
          <FormField
            label={isEs ? "Confirmar" : "Confirm"}
            name="confirm"
            required
            error={form.errors.confirm && (form.touched.confirm || form.submitCount > 0) ? form.errors.confirm : undefined}
          >
            <FormField.Input
              {...form.getFieldProps("confirm")}
              type="password"
              autoComplete="new-password"
              data-testid="profile-pw-confirm"
            />
          </FormField>
        </div>
        <div className="mt-4 flex justify-end">
          <SubmitButton
            submitting={form.submitting || m.isPending}
            testid="profile-change-pw-submit"
            className="bg-slate-800 hover:bg-slate-900 text-white"
            loadingText={isEs ? "Cambiando…" : "Changing…"}
          >
            <Lock className="h-4 w-4" />
            {isEs ? "Cambiar contraseña" : "Change password"}
          </SubmitButton>
        </div>
      </form>
    </Card>
  );
}

// ---------- DigestPreviewCard ----------
function DigestPreviewCard({ lang }) {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // iter-169zze · drive-by — restored missing `digestGate` definition.
  // The "Enviar ahora" button calls ``digestGate.run(...)`` (see below)
  // but the hook had been deleted by an earlier refactor, leaving the
  // page in a runtime-crash state on click.
  const digestGate = useGatedAction("weekly_digest");

  const sendNow = useMutation({
    mutationFn: () => digestService.sendNow(),
    onSuccess: (r) => {
      if (r?.sent) {
        toast.success(
          (tx(lang, "Digest sent to: ", "Resumen enviado a: ", "Resumen enviado a: ")) +
          (r.channels?.filter((c) => c.ok).map((c) => c.channel).join(", ") || "—")
        );
      } else if (r?.no_link_channels) {
        toast.info(
          tx(lang, "Link Telegram/WhatsApp on /integrations to receive the digest.", "Enlaza Telegram/WhatsApp en /integrations para recibir el resumen.", "Enlaza Telegram/WhatsApp en /integrations para recibir el resumen.")
        );
      } else {
        toast.info(
          tx(lang, "Nothing to digest this week yet.", "Aún no hay nada para resumir esta semana.", "Aún no hay nada para resumir esta semana.")
        );
      }
    },
    onError: () =>
      toast.error(tx(lang, "Digest send failed", "No se pudo enviar el resumen", "No se pudo enviar el resumen")),
  });

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await digestService.preview();
      setPreview(r);
    } catch {
      toast.error(tx(lang, "Preview failed", "No se pudo cargar la vista previa", "No se pudo cargar la vista previa"));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Card className="p-6 rounded-2xl border-slate-200 bg-gradient-to-br from-amber-50/40 to-white" data-testid="profile-digest-card">
      <div className="flex items-center gap-2 mb-2">
        <Send className="h-4 w-4 text-amber-600" />
        <h2 className="font-semibold text-slate-900">
          {tx(lang, "Weekly Lumfi digest", "Resumen semanal de Lumfi", "Resumen semanal de Lumfi")}
        </h2>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        {tx(lang, "Every Sunday 8pm UTC we push a recap (income, top expenses, upcoming bills, FX alerts and a pick) to your linked channels (Telegram/WhatsApp). Preview it or send it manually here.", "Cada domingo 8pm UTC enviamos un recap (ingresos, top de gastos, próximos pagos, alertas FX y una recomendación) a tus canales enlazados (Telegram/WhatsApp). Aquí puedes verlo o enviarlo manualmente.", "Cada domingo 8pm UTC enviamos un recap (ingresos, top de gastos, próximos pagos, alertas FX y una recomendación) a tus canales enlazados (Telegram/WhatsApp). Aquí puedes verlo o enviarlo manualmente.")}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="profile-digest-preview"
          variant="outline"
          onClick={doPreview}
          disabled={previewing}
          className="gap-2"
        >
          {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          {tx(lang, "Preview", "Vista previa", "Vista previa")}
        </Button>
        <Button
          data-testid="profile-digest-send"
          onClick={() => digestGate.run(() => sendNow.mutate())}
          disabled={sendNow.isPending}
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
        >
          {sendNow.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {tx(lang, "Send now", "Enviar ahora", "Enviar ahora")}
          <LockedBadge featureKey="weekly_digest" className="ml-1" />
        </Button>
      </div>
      {preview && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-white p-4" data-testid="profile-digest-preview-body">
          {preview.has_content ? (
            <pre className="text-xs text-slate-900 whitespace-pre-wrap font-sans leading-relaxed">{preview.body}</pre>
          ) : (
            <p className="text-xs text-slate-500 italic">
              {tx(lang, "Nothing to digest yet this week. Add an income, expense, or upcoming payment.", "Aún no hay nada que resumir esta semana. Registra algún ingreso, gasto o pago próximo.", "Aún no hay nada que resumir esta semana. Registra algún ingreso, gasto o pago próximo.")}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}


// ---------- MonthlyPdfEmailCard ----------
/**
 * iter-170af — Lets the user opt in/out of the automated monthly
 * Advanced Report PDF email (sent on the 1st of each month at 09:00
 * UTC by the ``monthly-advanced-pdf-email`` cron).
 *
 * Gated visually by the ``advanced_reports`` entitlement: if the
 * user's plan doesn't include the advanced report, the toggle is
 * disabled and a small hint points to /pricing. We still render the
 * card so the value prop is visible even for free users.
 */
function MonthlyPdfEmailCard({ lang, profile, qc }) {
  const { has, isLoading: entLoading } = useEntitlements();
  const entitled = has("advanced_reports");
  // Default ON when the field is missing — matches the cron's
  // server-side semantics.
  const enabled = profile?.monthly_pdf_email_enabled !== false;

  const toggle = useMutation({
    mutationFn: async (next) =>
      (await api.put("/profile", { monthly_pdf_email_enabled: next })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success(tx(lang, "Preferences updated",
        "Preferencias actualizadas", "Preferências atualizadas"));
    },
    onError: () => toast.error(tx(lang, "Save failed",
      "Error guardando", "Erro ao salvar")),
  });

  return (
    <Card
      className="p-6 rounded-2xl border-sky-200 bg-sky-50/40"
      data-testid="profile-monthly-pdf-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-sky-700" />
        <h2 className="font-semibold text-slate-900">
          {tx(lang, "Monthly advanced report by email",
            "Reporte avanzado mensual por email",
            "Relatório avançado mensal por email")}
        </h2>
        <LockedBadge featureKey="advanced_reports" className="ml-1" />
      </div>
      <p className="text-xs text-slate-600 mb-4 leading-snug">
        {tx(lang,
          "On the 1st of every month we email you a PDF with last month's advanced report: 6m trend, top categories, accounts, goals, debts and FX drift. Useful for your accountant or for your own archive.",
          "El primer día de cada mes te enviamos por email un PDF con el reporte avanzado del mes anterior: tendencia de 6m, top categorías, cuentas, metas, deudas y drift FX. Útil para tu contador o para tu archivo personal.",
          "No primeiro dia de cada mês enviamos por email um PDF com o relatório avançado do mês anterior: tendência de 6m, top categorias, contas, metas, dívidas e drift FX. Útil para seu contador ou seu arquivo.")}
      </p>
      <div className="flex items-center justify-between rounded-xl border border-sky-200 bg-white px-4 py-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900">
            {tx(lang, "Receive monthly report", "Recibir reporte mensual", "Receber relatório mensal")}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {entitled
              ? (enabled
                  ? tx(lang, "Active · next send on the 1st 09:00 UTC",
                      "Activo · próximo envío el día 1, 09:00 UTC",
                      "Ativo · próximo envio dia 1, 09:00 UTC")
                  : tx(lang, "Disabled — you'll keep getting the PDF on demand from /report",
                      "Desactivado — seguirás pudiendo descargar el PDF manualmente desde /reporte",
                      "Desativado — você poderá baixar o PDF manualmente em /reporte"))
              : tx(lang, "Requires the Advanced Reports feature in your plan.",
                  "Requiere la funcionalidad de Reportes avanzados en tu plan.",
                  "Requer Relatórios avançados no seu plano.")}
          </p>
        </div>
        <Switch
          checked={enabled && entitled}
          disabled={!entitled || entLoading || toggle.isPending}
          onCheckedChange={(v) => toggle.mutate(!!v)}
          data-testid="profile-monthly-pdf-toggle"
        />
      </div>
    </Card>
  );
}


// ---------- EmailPrivacyCard ----------
/**
 * <EmailPrivacyCard /> — user-facing email privacy preferences.
 *
 * Pulls the live Topic list (whatever the admin currently has
 * configured for the email service) and renders one toggle per
 * topic. Each toggle writes directly via
 * `PUT /api/profile/topic-preferences`, which also mirrors the legacy
 * `*_opt_in` flag onto the local profile when the topic is mapped
 * in admin config.
 *
 * Defaults: a contact that hasn't toggled anything yet is shown with
 * the topic's `default_subscription` ("opt_in" in practice for all of
 * Lumfi's marketing-style topics).
 *
 * The card also surfaces the hosted preference center URL so users
 * who want the full per-topic view can click through to the email
 * provider's hosted page.
 */
function EmailPrivacyCard({ lang, profile: _profile, qc }) {
  const isEs = lang === "es";
  const [sheetOpen, setSheetOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["profile", "topic-preferences"],
    queryFn: async () => (await api.get("/profile/topic-preferences")).data,
    retry: false,
    staleTime: 30_000,
  });

  const update = useMutation({
    mutationFn: async ({ topic_id, subscription }) =>
      (await api.put("/profile/topic-preferences", { topic_id, subscription })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile", "topic-preferences"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success(isEs ? "Preferencias actualizadas" : "Preferences updated");
    },
    onError: () => toast.error(isEs ? "Error guardando" : "Save failed"),
  });

  const topics = data?.topics || [];
  const apiKeyMissing = error?.response?.data?.detail === "resend_api_key_not_configured"
    || error?.response?.data?.detail === "resend_topics_unavailable";
  const subscribedCount = topics.filter((t) => t.subscription !== "opt_out").length;
  const totalCount = topics.length;

  // Compact summary line: tells the user the current state without
  // forcing them to scan a long toggle list. The BottomSheet handles
  // the per-topic detail when (and only when) they ask for it.
  const summary = (() => {
    if (isLoading) return isEs ? "Cargando…" : "Loading…";
    if (apiKeyMissing) {
      return isEs
        ? "Aún no disponibles. Pídele al admin que configure el servicio de email."
        : "Not available yet. Ask your admin to configure the email service.";
    }
    if (totalCount === 0) {
      return isEs
        ? "Sin categorías configuradas todavía."
        : "No categories configured yet.";
    }
    if (subscribedCount === 0) {
      return isEs
        ? "Estás dado de baja de todas las categorías."
        : "You're opted out of every category.";
    }
    return isEs
      ? `Estás suscrito a ${subscribedCount} de ${totalCount} categoría${totalCount === 1 ? "" : "s"}.`
      : `Subscribed to ${subscribedCount} of ${totalCount} categor${totalCount === 1 ? "y" : "ies"}.`;
  })();

  return (
    <>
      <Card className="p-6 rounded-2xl border-slate-200" data-testid="profile-privacy-card">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-4 w-4 text-emerald-700" />
          <h2 className="font-semibold text-slate-900">
            {isEs ? "Privacidad y preferencias de email" : "Privacy & email preferences"}
          </h2>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          {isEs
            ? "Elige qué tipos de email quieres recibir. Las transaccionales críticas (cambios de contraseña, recibos) siempre se mandan."
            : "Choose which email types you want to receive. Critical transactional emails (password changes, receipts) are always sent."}
        </p>

        {/* Compact summary row — replaces the previously inline toggle list.
            Tap "Gestionar" to open the BottomSheet with the per-topic switches. */}
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
          data-testid="privacy-summary-row"
        >
          <div className="flex items-center gap-2 min-w-0">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500 shrink-0" />
            ) : apiKeyMissing || totalCount === 0 ? (
              <BellOff className="h-4 w-4 text-amber-600 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            )}
            <span
              className="text-xs text-slate-700 truncate"
              data-testid="privacy-summary-text"
            >
              {summary}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 h-8 px-3 text-xs gap-1"
            disabled={isLoading || apiKeyMissing}
            onClick={() => setSheetOpen(true)}
            data-testid="privacy-open-sheet"
          >
            <Settings2 className="h-3.5 w-3.5" />
            {isEs ? "Gestionar" : "Manage"}
          </Button>
        </div>

        <div className="mt-3 text-xs text-slate-500 flex items-center justify-end">
          <Link
            to="/preferences"
            data-testid="privacy-prefs-link"
            className="text-emerald-700 font-semibold hover:text-emerald-800 inline-flex items-center gap-1"
          >
            {isEs ? "Página dedicada →" : "Dedicated page →"}
          </Link>
        </div>
      </Card>

      <BottomSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title={isEs ? "Preferencias de email" : "Email preferences"}
        description={isEs
          ? "Activa o desactiva cada categoría. Los cambios se guardan al instante."
          : "Turn each category on or off. Changes save instantly."}
        size="md"
        testid="privacy-bottom-sheet"
      >
        {topics.length === 0 ? (
          <div className="text-center py-6 text-sm text-slate-400" data-testid="privacy-sheet-empty">
            {isEs
              ? "Sin categorías configuradas todavía."
              : "No categories configured yet."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="privacy-topic-list">
            {topics.map((t) => (
              <PrefRow
                key={t.id}
                label={t.name}
                hint={t.description}
                checked={t.subscription !== "opt_out"}
                disabled={update.isPending}
                onChange={(v) =>
                  update.mutate({ topic_id: t.id, subscription: v ? "opt_in" : "opt_out" })
                }
                testid={`privacy-topic-${t.id}`}
              />
            ))}
          </div>
        )}
      </BottomSheet>
    </>
  );
}

function PrefRow({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <label className="flex items-start justify-between gap-3 py-3 cursor-pointer group">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${
          checked ? "bg-emerald-600 border-emerald-700" : "bg-slate-200 border-slate-300"
        } disabled:opacity-50`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}

